/**
 * Lectura del Excel de facturación del mall ("ANALISIS FACTURACION"): una hoja por local
 * con cabecera (local, m², RUT…) y una tabla mensual con lo facturado (UF, renta fija,
 * venta del mes anterior, variable, gasto común, publicidad).
 *
 * Las tarifas vigentes se deducen de los montos de cada mes (la cabecera suele estar
 * desactualizada) y se agrupan en tramos: meses seguidos con la misma tarifa.
 */
import { periodoAnterior } from "./facturacion";

export interface MesExcel {
  periodo: string; // YYYY-MM
  uf: number;
  fijo: number;
  venta: number; // venta del mes anterior
  calculoVariable: number;
  diferencia: number; // variable cobrado
  gc: number;
  publicidad: number;
  publicidadDiferencia: number;
  /** Otros cobros del mes por columna extra (bodega, internet…): descripción → monto. */
  extras: Record<string, number>;
}

/** Cargo extra del contrato leído de una columna propia del Excel (bodega, internet…). */
export interface OtroCobroImportado {
  descripcion: string;
  exento: boolean;
  desde: string; // YYYY-MM
  hasta: string; // YYYY-MM
  /** Monto fijo en UF; si no varía con la UF, en pesos. */
  uf?: number;
  pesos?: number;
}

export interface TramoImportado {
  desde: string; // YYYY-MM
  hasta: string; // YYYY-MM
  fijoUFm2: number;
  gcUFm2: number;
  promocionPct: number;
  /** Cobro en pesos (no indexado): el monto se repite aunque cambie la UF. $ por m² al mes. */
  fijoPesosM2?: number;
  gcPesosM2?: number;
}

export interface HojaImportada {
  hoja: string;
  rut: string;
  numeroLocal: string;
  m2: number;
  /** Sin m² en el Excel: cobro por unidad (superficie 1). */
  porUnidad: boolean;
  porcentajeVariable: number;
  operadorVariable: "OPCION" | "SIN MINIMO";
  /** ALQUILER = publicidad sobre fijo + variable; ALQUILER FIJO = solo sobre el fijo. */
  promocionOrigen: "ALQUILER" | "ALQUILER FIJO";
  tramos: TramoImportado[];
  /** Día de inicio si el primer mes vino prorrateado (apertura a mitad de mes). */
  diaInicio: number;
  /** Último mes con montos si el contrato dejó de cobrarse antes del fin del Excel. */
  terminoPeriodo: string | null;
  ventas: Array<{ periodo: string; venta: number }>;
  otrosCobros: OtroCobroImportado[];
  notas: string;
  advertencias: string[];
}

export interface ResultadoLectura {
  hojas: HojaImportada[];
  omitidas: Array<{ hoja: string; motivo: string }>;
}

const HOJAS_RESUMEN = ["MENU", "OPERADORES"];
const TOL_TARIFA = 0.0005;

const texto = (v: unknown) => String(v ?? "").replace(/\s+/g, " ").trim();
const mayus = (v: unknown) => texto(v).toUpperCase();
const num = (v: unknown): number => {
  if (typeof v === "number") return isFinite(v) ? v : 0;
  const n = Number(String(v ?? "").replace(",", "."));
  return isFinite(n) ? n : 0;
};
const r6 = (x: number) => Math.round(x * 1e6) / 1e6;
const r4 = (x: number) => Math.round(x * 1e4) / 1e4;

/** Periodo YYYY-MM de una celda de fecha (Date de Excel o texto). */
export function periodoDeCelda(v: unknown): string {
  if (v instanceof Date && !isNaN(v.getTime())) {
    // Excel guarda medianoche; se suma medio día para no caer en el mes anterior por zona horaria.
    return new Date(v.getTime() + 12 * 3600e3).toISOString().slice(0, 7);
  }
  const t = texto(v);
  let m = t.match(/^(\d{4})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}`;
  m = t.match(/^\d{1,2}[-/](\d{1,2})[-/](\d{4})/);
  return m ? `${m[2]}-${m[1].padStart(2, "0")}` : "";
}

export const normalizarRut = (rut: string): string => String(rut || "").replace(/[^0-9kK]/g, "").toUpperCase();

/** "FACTURAR \"RENTA FIJA\" BODEGA" → "Bodega"; "INTERNET 3 UF MENSUALES EXENTA" → "Internet". */
export function descripcionOtroCobro(encabezado: string): string {
  const limpio = mayus(encabezado)
    .replace(/FACTURAR|"|RENTA FIJA|EXENTA|MENSUAL(ES)?|\d+([.,]\d+)?\s*UF/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const t = limpio || "Otro cobro";
  return t.charAt(0) + t.slice(1).toLowerCase();
}

/** Tabla mensual de una hoja (fila con MES y UF). null si no la tiene. */
export function leerMeses(rows: unknown[][]): { iTabla: number; meses: MesExcel[] } | null {
  const iTabla = rows.findIndex((r) => r.some((c) => mayus(c) === "MES") && r.some((c) => mayus(c) === "UF"));
  if (iTabla < 0) return null;
  const h = rows[iTabla].map(mayus);
  const col = (t: string) => h.findIndex((x) => x.includes(t));
  const k = {
    mes: h.indexOf("MES"),
    uf: h.indexOf("UF"),
    fijo: col("RENTA FIJA"),
    venta: col("VENTA MES"),
    calc: col("CALCULO VARIABLE"),
    dif: col("DIFERENCIA"),
    gc: col("G.C."),
    pub: h.findIndex((x) => x === 'FACTURAR "PROM. PUBLICIDAD"'),
    pubDif: col("PROM. PUB. DIF"),
  };
  const valor = (r: unknown[], i: number) => (i >= 0 ? num(r[i]) : 0);

  // Columnas extra con montos: otra renta fija (bodega) o cargos como internet. Los consumos
  // (luz, agua, gas) quedan fuera: se facturan aparte.
  const usadas = new Set(Object.values(k));
  const extrasCols = h
    .map((t, i) => ({ t, i }))
    .filter(({ t, i }) => t && !usadas.has(i) && (t.includes("RENTA FIJA") || t.includes("EXENTA") || t.includes("INTERNET")))
    .filter(({ t }) => !/ELECTRICIDAD|AGUA|GAS|BASURA/.test(t))
    .map(({ t, i }) => ({ i, descripcion: descripcionOtroCobro(t), exento: t.includes("EXENTA") }));

  const todos: MesExcel[] = rows
    .slice(iTabla + 1)
    .map((r) => ({
      periodo: periodoDeCelda(r[k.mes]),
      uf: valor(r, k.uf),
      fijo: valor(r, k.fijo),
      venta: valor(r, k.venta),
      calculoVariable: valor(r, k.calc),
      diferencia: valor(r, k.dif),
      gc: valor(r, k.gc),
      publicidad: valor(r, k.pub),
      publicidadDiferencia: valor(r, k.pubDif),
      extras: Object.fromEntries(extrasCols.map((c) => [`${c.descripcion}${c.exento ? "|exento" : ""}`, valor(r, c.i)])),
    }))
    // Sin UF = mes futuro o no ocurrido (el Excel trae filas proyectadas): se ignora.
    .filter((m) => /^\d{4}-\d{2}$/.test(m.periodo) && m.uf > 0);
  return { iTabla, meses: todos };
}

/**
 * En locales sin fijo, el Excel a veces deja el variable en 0 pero cobra la publicidad calculada
 * sobre él (Air Park, Muro Escalada): Softland sí factura ese variable, así que la publicidad es su %.
 * Solo aplica si el local nunca tiene fijo: con fijo, la publicidad es sobre el fijo (MAV Sports).
 */
const publicidadSobreVariableNoCobrado = (m: MesExcel): boolean =>
  m.fijo === 0 && m.diferencia === 0 && m.calculoVariable > 0 && m.publicidad + m.publicidadDiferencia > 0;

/** Lee una hoja de operador. Devuelve null si no tiene la tabla mensual (MES / UF). */
export function leerHojaOperador(
  hoja: string,
  rows: unknown[][],
  opciones: {
    desde: string;
    hasta?: string;
    /** Último mes con montos en todo el libro: si la hoja termina antes, el contrato terminó. */
    ultimoPeriodoLibro?: string;
    /** UF más usada en cada mes del libro: si la hoja usa otra, se advierte (día UF distinto). */
    ufComun?: Record<string, number>;
  }
): HojaImportada | null {
  const tabla = leerMeses(rows);
  if (!tabla) return null;
  const { iTabla, meses: todos } = tabla;
  const enRango = todos.filter(
    (m) =>
      m.periodo >= opciones.desde &&
      (!opciones.hasta || m.periodo <= opciones.hasta) &&
      m.fijo + m.diferencia + m.gc + m.publicidad + m.publicidadDiferencia + m.venta > 0
  );

  // Cabecera: fila con "SUP. M2"; los datos van en la fila siguiente (a veces corrida una columna).
  const iCab = rows.findIndex((r) => r.some((c) => mayus(c).includes("SUP. M2")));
  let m2 = 0;
  let numeroLocal = "";
  if (iCab >= 0) {
    const lab = rows[iCab].map(mayus);
    const iM2 = lab.findIndex((x) => x.includes("SUP. M2"));
    const iNum = lab.findIndex((x) => x.includes("NÚMERO ACTUAL") || x.includes("NUMERO ACTUAL"));
    for (const r of rows.slice(iCab + 1, iCab + 4)) {
      const d = [0, -1, 1].find((x) => typeof r[iM2 + x] === "number" && (r[iM2 + x] as number) > 0);
      if (d !== undefined) {
        m2 = r[iM2 + d] as number;
        numeroLocal = iNum >= 0 ? texto(r[iNum + d]) : "";
        break;
      }
    }
  }
  const cabeceraTexto = rows.slice(0, Math.max(iTabla, 1)).flat().map(texto).join(" ");
  const rut = (cabeceraTexto.match(/\d{1,2}\.?\d{3}\.?\d{3}\s*-\s*[\dkK]/) || [""])[0].replace(/\s/g, "");
  const notas = rows
    .slice(0, iTabla)
    .flat()
    .map(texto)
    .filter((s) => /\*|mes \d|año|a partir|descuento|vigencia|abri/i.test(s))
    .join(" · ");

  const advertencias: string[] = [];
  if (!rut) advertencias.push("sin RUT en la hoja");
  const porUnidad = !(m2 > 0);
  const superficie = porUnidad ? 1 : m2;
  if (porUnidad) advertencias.push("sin m²: se importa como cobro por unidad");

  // Variable: % = cálculo / venta (el primero disponible); OPCION si el contrato tiene fijo.
  const pcts = enRango.filter((m) => m.venta > 0 && m.calculoVariable > 0).map((m) => r4(m.calculoVariable / m.venta));
  const porcentajeVariable = pcts[0] || 0;
  if (new Set(pcts).size > 1) advertencias.push(`el % variable cambia entre meses (${Array.from(new Set(pcts)).map((p) => p * 100 + "%").join(", ")}); se usa el primero`);
  const tieneFijo = enRango.some((m) => m.fijo > 0);

  // Publicidad: sobre fijo + variable si alguna vez se cobró sobre la diferencia,
  // o si no hay fijo y la publicidad sale del variable.
  const promocionOrigen = enRango.some(
    (m) =>
      m.publicidadDiferencia > 0 ||
      (m.fijo === 0 && ((m.diferencia > 0 && m.publicidad > 0) || (!tieneFijo && publicidadSobreVariableNoCobrado(m))))
  )
    ? "ALQUILER"
    : "ALQUILER FIJO";

  const tarifa = (m: MesExcel): Omit<TramoImportado, "desde" | "hasta"> => ({
    fijoUFm2: m.fijo > 0 ? r6(m.fijo / (m.uf * superficie)) : 0,
    gcUFm2: m.gc > 0 ? r6(m.gc / (m.uf * superficie)) : 0,
    promocionPct:
      m.fijo > 0 && m.publicidad > 0
        ? r4(m.publicidad / m.fijo)
        : m.diferencia > 0 && m.publicidad + m.publicidadDiferencia > 0
        ? r4((m.publicidad + m.publicidadDiferencia) / m.diferencia)
        : !tieneFijo && publicidadSobreVariableNoCobrado(m)
        ? r4((m.publicidad + m.publicidadDiferencia) / m.calculoVariable)
        : 0,
  });
  // Un cargo es "el mismo" si mantiene la tarifa en UF o, con la UF cambiando, mantiene el
  // monto en pesos (cobro no indexado). Esto último se marca en el tramo.
  type Cargo = "fijo" | "gc";
  const tramos: Array<TramoImportado & { montos: Record<Cargo, number>; uf: number }> = [];
  const mismoCargo = (ult: (typeof tramos)[number], m: MesExcel, c: Cargo, rateNuevo: number) => {
    const rateUlt = c === "fijo" ? ult.fijoUFm2 : ult.gcUFm2;
    const pesos = c === "fijo" ? ult.fijoPesosM2 : ult.gcPesosM2;
    if (pesos !== undefined) return Math.abs(m[c] - ult.montos[c]) <= 1;
    // Mismo monto con la UF cambiando = cobro en pesos. Va antes que la tarifa: con tarifas
    // chicas, la diferencia en UF/m² cabe en la tolerancia.
    if (ult.montos[c] > 0 && Math.abs(m[c] - ult.montos[c]) <= 1 && Math.abs(m.uf - ult.uf) > 1) return "pesos";
    return Math.abs(rateUlt - rateNuevo) < TOL_TARIFA;
  };
  for (const m of enRango) {
    const t = tarifa(m);
    const ult = tramos[tramos.length - 1];
    const fijo = ult ? mismoCargo(ult, m, "fijo", t.fijoUFm2) : false;
    const gc = ult ? mismoCargo(ult, m, "gc", t.gcUFm2) : false;
    const promoIgual =
      ult && (t.promocionPct === 0 || ult.promocionPct === 0 || Math.abs(ult.promocionPct - t.promocionPct) < TOL_TARIFA);
    if (ult && fijo && gc && promoIgual) {
      ult.hasta = m.periodo;
      if (!ult.promocionPct) ult.promocionPct = t.promocionPct;
      if (fijo === "pesos") ult.fijoPesosM2 = r6(m.fijo / superficie);
      if (gc === "pesos") ult.gcPesosM2 = r6(m.gc / superficie);
      ult.montos = { fijo: m.fijo, gc: m.gc };
      ult.uf = m.uf;
    } else tramos.push({ desde: m.periodo, hasta: m.periodo, ...t, montos: { fijo: m.fijo, gc: m.gc }, uf: m.uf });
  }
  if (tramos.some((t) => t.fijoPesosM2 !== undefined || t.gcPesosM2 !== undefined))
    advertencias.push("cobro en pesos (no varía con la UF) en el fijo o el gasto común");

  // Apertura a mitad de mes: el primer mes viene prorrateado → día de inicio en vez de tramo.
  let diaInicio = 1;
  if (tramos.length > 1 && tramos[0].desde === tramos[0].hasta && tramos[0].fijoUFm2 > 0) {
    const ratio = tramos[0].fijoUFm2 / tramos[1].fijoUFm2;
    const gcOk = !tramos[1].gcUFm2 || Math.abs(tramos[0].gcUFm2 / tramos[1].gcUFm2 - ratio) < 0.02;
    if (ratio > 0 && ratio < 0.98 && gcOk) {
      const [y, mm] = tramos[0].desde.split("-").map(Number);
      const diasMes = new Date(y, mm, 0).getDate();
      diaInicio = Math.min(diasMes, Math.max(1, diasMes - Math.round(ratio * diasMes) + 1));
      tramos[1].desde = tramos[0].desde;
      tramos.shift();
      advertencias.push(`primer mes prorrateado: inicio estimado el día ${diaInicio}`);
    }
  }

  // Otros cobros: un cargo por columna extra con su tarifa (UF fija o, si no varía, pesos).
  const otrosCobros: OtroCobroImportado[] = [];
  const claves = Array.from(new Set(enRango.flatMap((m) => Object.keys(m.extras))));
  for (const clave of claves) {
    const meses = enRango.filter((m) => (m.extras[clave] || 0) > 0);
    if (!meses.length) continue;
    const [descripcion, marca] = clave.split("|");
    const montos = meses.map((m) => m.extras[clave]);
    const enPesos = meses.length > 1 && Math.max(...montos) - Math.min(...montos) <= 1 && meses[0].uf !== meses[meses.length - 1].uf;
    const ultimo = meses[meses.length - 1];
    otrosCobros.push({
      descripcion,
      exento: marca === "exento",
      desde: meses[0].periodo,
      hasta: ultimo.periodo,
      ...(enPesos ? { pesos: Math.round(ultimo.extras[clave]) } : { uf: r4(ultimo.extras[clave] / ultimo.uf) }),
    });
    advertencias.push(
      `otro cobro "${descripcion}"${marca === "exento" ? " (exento)" : ""}: ${enPesos ? "$" + Math.round(ultimo.extras[clave]) : r4(ultimo.extras[clave] / ultimo.uf) + " UF"} al mes`
    );
  }

  const ventas = enRango
    .filter((m) => m.venta > 0)
    .map((m) => ({ periodo: periodoAnterior(m.periodo), venta: Math.round(m.venta) }));

  // Contrato que dejó de cobrarse antes del último mes del libro.
  const conMontos = enRango.filter((m) => m.fijo + m.diferencia + m.gc + m.publicidad + m.publicidadDiferencia > 0);
  const ultimoConMontos = conMontos.length ? conMontos[conMontos.length - 1].periodo : null;
  const terminoPeriodo =
    ultimoConMontos && opciones.ultimoPeriodoLibro && ultimoConMontos < opciones.ultimoPeriodoLibro ? ultimoConMontos : null;
  if (terminoPeriodo) advertencias.push(`sin cobros después de ${terminoPeriodo}: se importa con término en ese mes`);

  // UF distinta a la que usa el resto del libro ese mes (otro día de UF).
  if (opciones.ufComun) {
    const distintos = enRango.filter((m) => m.uf > 0 && opciones.ufComun?.[m.periodo] && Math.abs(m.uf - (opciones.ufComun[m.periodo] as number)) > 0.01);
    if (distintos.length)
      advertencias.push(`usa una UF distinta a la del resto en ${distintos.length} mes(es) (p. ej. ${distintos[0].periodo}: $${distintos[0].uf}); revisa el día de la UF`);
  }
  // Variable con % pero que el Excel no cobró en meses con venta (posible acuerdo especial).
  const sinCobrar = enRango.filter((m) => m.venta > 0 && m.calculoVariable > 0 && m.fijo === 0 && m.diferencia === 0);
  if (!tieneFijo && sinCobrar.some(publicidadSobreVariableNoCobrado))
    advertencias.push(
      `el Excel no anota el variable en ${sinCobrar.length} mes(es), pero su publicidad sale de él (Softland sí lo cobra): se importan el variable y la publicidad sobre él`
    );
  else if (sinCobrar.length) advertencias.push(`el Excel no cobró el variable en ${sinCobrar.length} mes(es) con venta; revisa el contrato`);

  return {
    hoja,
    rut,
    numeroLocal,
    m2: porUnidad ? 0 : m2,
    porUnidad,
    porcentajeVariable,
    operadorVariable: tieneFijo ? "OPCION" : "SIN MINIMO",
    promocionOrigen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tramos: tramos.map(({ montos, uf, ...t }) => t),
    diaInicio,
    terminoPeriodo,
    ventas,
    otrosCobros,
    notas,
    advertencias,
  };
}

/** Lee todas las hojas de operador del libro (se omiten MENU y OPERADORES). */
export function leerExcelFacturacion(
  hojas: Record<string, unknown[][]>,
  opciones: { desde: string; hasta?: string }
): ResultadoLectura {
  const resultado: ResultadoLectura = { hojas: [], omitidas: [] };
  const operadores = Object.entries(hojas).filter(([nombre]) => !HOJAS_RESUMEN.includes(mayus(nombre)));
  // Pasada previa: último mes con montos del libro y UF más usada por mes.
  let ultimoPeriodoLibro = "";
  const conteoUf: Record<string, Map<number, number>> = {};
  for (const [, rows] of operadores) {
    for (const m of leerMeses(rows)?.meses || []) {
      if (m.fijo + m.diferencia + m.gc + m.publicidad + m.publicidadDiferencia <= 0) continue;
      if (m.periodo > ultimoPeriodoLibro) ultimoPeriodoLibro = m.periodo;
      const mapa = (conteoUf[m.periodo] = conteoUf[m.periodo] || new Map());
      mapa.set(m.uf, (mapa.get(m.uf) || 0) + 1);
    }
  }
  const ufComun: Record<string, number> = {};
  for (const [periodo, mapa] of Object.entries(conteoUf))
    ufComun[periodo] = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1])[0][0];
  for (const [nombre, rows] of operadores) {
    const hoja = leerHojaOperador(nombre, rows, { ...opciones, ultimoPeriodoLibro, ufComun });
    if (!hoja) resultado.omitidas.push({ hoja: nombre, motivo: "no tiene la tabla mensual (MES / UF)" });
    else if (!hoja.tramos.length && !hoja.ventas.length)
      resultado.omitidas.push({ hoja: nombre, motivo: `sin montos desde ${opciones.desde}` });
    else resultado.hojas.push(hoja);
  }
  return resultado;
}

/** Razón social por RUT desde la hoja MENU (NOMBRE LOCAL · RAZON SOCIAL · RUT). */
export function razonesSocialesDelMenu(rows: unknown[][]): Map<string, string> {
  const mapa = new Map<string, string>();
  for (const r of rows) {
    const celdas = r.map(texto).filter(Boolean);
    const rut = celdas.find((c) => /\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]/.test(c));
    if (!rut) continue;
    const i = celdas.indexOf(rut);
    if (i > 0) mapa.set(normalizarRut(rut), celdas[i - 1]);
  }
  return mapa;
}

/** "YYYY-MM" → "DD-MM-YYYY" del primer o último día, formato de las condiciones comerciales. */
export function fechaCondicion(periodo: string, extremo: "inicio" | "fin", dia = 1): string {
  const [y, m] = periodo.split("-").map(Number);
  const d = extremo === "inicio" ? dia : new Date(y, m, 0).getDate();
  return `${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}-${y}`;
}

// ---------------------------------------------------------------------------
// Planificación: qué se crea en el sistema a partir de las hojas leídas.
// ---------------------------------------------------------------------------

interface ClienteExistente {
  id?: string;
  rut: string;
  nombre: string;
  [k: string]: unknown;
}
interface LocalExistente {
  id?: string;
  codigo: string;
  [k: string]: unknown;
}
interface ContratoExistente {
  id?: string;
  codigo: number;
  localId: string;
  estadoContrato: string;
}

export interface OpcionesImportacion {
  diaUf: number;
  centroCosto: string;
  liquidacion: string;
  /** Término de los contratos importados (DD-MM-YYYY); la última condición queda abierta hasta ahí. */
  fechaTermino: string;
  cargarVentas: boolean;
}

export interface FilaPlan {
  hoja: HojaImportada;
  cliente: { id: string; nombre: string; rut: string; nuevo: boolean };
  local: { id: string; codigo: string; nuevo: boolean };
  /** Código del contrato vigente que ya existe para el local (no se duplica). */
  contratoExistente: number | null;
  codigo: number;
}

const UF_INDEXACION = "UNIDAD DE FOMENTO - INDEXACION";
const normCodigo = (s: string) => String(s || "").replace(/\s+/g, "").toUpperCase();

/** Arma las filas de la vista previa: cruces con clientes y locales existentes y códigos nuevos. */
export function planificarImportacion(
  hojas: HojaImportada[],
  datos: {
    clientes: ClienteExistente[];
    locales: LocalExistente[];
    contratos: ContratoExistente[];
    razonesSociales: Map<string, string>;
    generarId: () => string;
  }
): FilaPlan[] {
  let codigo = Math.max(0, ...datos.contratos.map((c) => Number(c.codigo) || 0));
  const clientesNuevos = new Map<string, FilaPlan["cliente"]>();
  const localesNuevos = new Map<string, FilaPlan["local"]>();
  return hojas.map((hoja) => {
    const rut = normalizarRut(hoja.rut);
    const existente = rut ? datos.clientes.find((c) => normalizarRut(c.rut) === rut) : undefined;
    let cliente: FilaPlan["cliente"];
    if (existente) cliente = { id: existente.id ?? "", nombre: existente.nombre, rut: existente.rut, nuevo: false };
    else {
      const clave = rut || `hoja:${hoja.hoja}`;
      cliente =
        clientesNuevos.get(clave) ||
        { id: datos.generarId(), nombre: datos.razonesSociales.get(rut) || hoja.hoja, rut: hoja.rut, nuevo: true };
      clientesNuevos.set(clave, cliente);
    }
    const codigoLocal = hoja.numeroLocal || hoja.hoja;
    const localExistente = datos.locales.find((l) => normCodigo(l.codigo) === normCodigo(codigoLocal));
    let local: FilaPlan["local"];
    if (localExistente) local = { id: localExistente.id ?? "", codigo: localExistente.codigo, nuevo: false };
    else {
      local = localesNuevos.get(normCodigo(codigoLocal)) || { id: datos.generarId(), codigo: codigoLocal, nuevo: true };
      localesNuevos.set(normCodigo(codigoLocal), local);
    }
    const vigente = datos.contratos.find((c) => c.localId === local.id && c.estadoContrato === "Vigente");
    return { hoja, cliente, local, contratoExistente: vigente ? vigente.codigo : null, codigo: vigente ? 0 : ++codigo };
  });
}

export interface DocumentosImportacion {
  clientes: Array<Record<string, unknown>>;
  locales: Array<Record<string, unknown>>;
  contratos: Array<Record<string, unknown>>;
  condiciones: Array<Record<string, unknown>>;
  divisiones: Array<Record<string, unknown>>;
  ventas: Array<Record<string, unknown>>;
}

/**
 * Documentos a guardar para las filas elegidas. Las fechas del contrato se devuelven
 * como Date (local) para convertirlas a Timestamp al escribir.
 */
export function documentosImportacion(filas: FilaPlan[], opciones: OpcionesImportacion): DocumentosImportacion {
  const docs: DocumentosImportacion = { clientes: [], locales: [], contratos: [], condiciones: [], divisiones: [], ventas: [] };
  const vistos = new Set<string>();
  for (const f of filas) {
    if (f.contratoExistente !== null || !f.hoja.tramos.length) continue;
    const h = f.hoja;
    const superficie = h.porUnidad ? 1 : h.m2;
    const clienteDoc = { id: f.cliente.id, codigo: f.cliente.id, nombre: f.cliente.nombre, rut: f.cliente.rut, giro: "", telefono: "", direccion: "", correo: "", rubro: "", nombre_comercial: h.hoja };
    const localDoc = { id: f.local.id, codigo: f.local.codigo, centroCosto: opciones.centroCosto, unidadLocativa: h.hoja, superficie, nivel: "", fachada: "", vista_planta: "", tipo_local: h.porUnidad ? "UNIDAD" : "LOCAL", estadoLocal: "ARRENDADO" };
    if (f.cliente.nuevo && !vistos.has("c" + f.cliente.id)) docs.clientes.push(clienteDoc);
    if (f.local.nuevo && !vistos.has("l" + f.local.id)) docs.locales.push(localDoc);
    vistos.add("c" + f.cliente.id);
    vistos.add("l" + f.local.id);

    const [yI, mI] = h.tramos[0].desde.split("-").map(Number);
    // Término: el del Excel si dejó de cobrarse antes; si no, el que se elige al importar.
    const termino = h.terminoPeriodo ? fechaCondicion(h.terminoPeriodo, "fin") : opciones.fechaTermino;
    const [dF, mF, yF] = termino.split("-").map(Number);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _ic, ...clienteSnapshot } = clienteDoc;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _il, ...localSnapshot } = localDoc;
    docs.contratos.push({
      codigo: f.codigo, clienteId: f.cliente.id, localId: f.local.id, clienteSnapshot, localSnapshot,
      superficieTotal: superficie, dia_uf: opciones.diaUf, facturacion: "Mensual", renovacion: "Manual",
      nombreResponsable: "", telefonoResponsable: "", pdfContratoUrl: "", centroCostoContrato: opciones.centroCosto,
      estadoContrato: "Vigente", fechaInicio: new Date(yI, mI - 1, h.diaInicio), fechaFin: new Date(yF, mF - 1, dF),
      origenImportacion: `Excel facturación · ${h.hoja}`,
    });

    h.tramos.forEach((t, i) => {
      const n = i + 1;
      const ultimo = i === h.tramos.length - 1;
      docs.condiciones.push({
        codigo_contrato: f.codigo, codigo_condicion: n,
        desde: fechaCondicion(t.desde, "inicio", i === 0 ? h.diaInicio : 1),
        hasta: ultimo ? termino : fechaCondicion(t.hasta, "fin"),
        liquidacion: opciones.liquidacion,
      });
      const base = { codigo_contrato: f.codigo, codigo_condicion: n, moneda: "PESOS CHILENOS", escala: "DIRECTA", indexacion: UF_INDEXACION, aporte: "NO APLICA", operador: "SIN MINIMO", origen: "NO APLICA", minimo: 0 };
      const tieneFijo = t.fijoUFm2 > 0 || t.fijoPesosM2 !== undefined;
      const enPesos = (pesos?: number) => (pesos !== undefined ? { indexacion: "PESOS CHILENOS", minimo: pesos } : {});
      if (tieneFijo)
        docs.divisiones.push({ ...base, codigo_division: 1, nombre_division: "Alquiler Fijo", minimo: t.fijoUFm2, ...enPesos(t.fijoPesosM2) });
      if (h.porcentajeVariable)
        docs.divisiones.push({ ...base, codigo_division: 2, nombre_division: "Alquiler Variable", porcentaje: h.porcentajeVariable, operador: tieneFijo ? "OPCION" : "SIN MINIMO", origen: "VEN/NETAS" });
      if (t.gcUFm2 > 0 || t.gcPesosM2 !== undefined)
        docs.divisiones.push({ ...base, codigo_division: 3, nombre_division: "Gastos Comunes", minimo: t.gcUFm2, ...enPesos(t.gcPesosM2) });
      if (t.promocionPct > 0)
        docs.divisiones.push({ ...base, codigo_division: 4, nombre_division: "Fdo. Promocion Fijo", porcentaje: t.promocionPct, origen: h.promocionOrigen });
      // Otros cobros que rigen en algún mes del tramo (bodega, internet…).
      (h.otrosCobros || [])
        .filter((o) => o.desde <= t.hasta && (ultimo || o.hasta >= t.desde))
        .forEach((o, j) =>
          docs.divisiones.push({
            ...base,
            codigo_division: 5 + j,
            nombre_division: "Otro Cobro",
            descripcion: o.descripcion,
            base: "MONTO FIJO",
            exento: o.exento,
            ...(o.pesos !== undefined ? { indexacion: "PESOS CHILENOS", minimo: o.pesos } : { minimo: o.uf }),
          })
        );
    });

    if (opciones.cargarVentas)
      for (const v of h.ventas)
        docs.ventas.push({ id: `${f.codigo}-${v.periodo}`, codigoContrato: f.codigo, periodo: v.periodo, ventaNeta: v.venta, fuente: `Excel facturación · ${h.hoja}` });
  }
  return docs;
}
